/**
 * @fileoverview Renews and ends Firebase sessions started on the shared Next.js `/login` page.
 *
 * Matches `swing-left/frontend`'s `lib/firebase.ts`: the `[DEFAULT]` app name, the tenant and
 * `browserLocalPersistence`. `/v1/auth/firebase-config` must serve the Next.js build's API key,
 * because both apps share the stored user at `firebase:authUser:<apiKey>:[DEFAULT]`.
 *
 * Avoid `getAuth`: its IndexedDB-first persistence would move the user out of localStorage and sign
 * the Next.js app out. The SDK loads on first use, so Auth0 sessions never download it.
 */

import axios from 'axios';

import isNonEmptyString from './functions/isNonEmptyString';
import logger from './logger';

/** Firebase errors that mean signing in again; any other failure may be temporary. */
const SIGNED_OUT_ERROR_CODES = [
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-user-token',
];

const APP_NAME = '[DEFAULT]';
const CONFIG_TIMEOUT_MS = 8000;
const RESTORE_TIMEOUT_MS = 10000;
const SIGN_OUT_TIMEOUT_MS = 5000;
const WEB_CONFIG_FIELDS = ['apiKey', 'authDomain', 'projectId', 'tenantId'];
const PERSISTED_USER_KEY_PREFIX = 'firebase:authUser:';
const PERSISTED_USER_KEY_SUFFIX = `:${ APP_NAME }`;

/** Thrown when Firebase has no usable signed-in user for this browser, so a new login is needed. */
class FirebaseSignedOutError extends Error {
  constructor(message = 'no signed-in Firebase user') {
    super(message);
    this.name = 'FirebaseSignedOutError';
  }
}

let webConfigPromise;
let authPromise;

/**
 * Rejects with `message` when `promise` has not settled within `ms`.
 *
 * @param {Promise} promise - The operation.
 * @param {number} ms - The deadline.
 * @param {string} message - The timeout error's message.
 * @returns {Promise} The operation's result.
 */
function withTimeout(promise, ms, message) {
  let timeout;
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      timeout = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]).finally(() => clearTimeout(timeout));
}

/**
 * Fetches the Firebase web configuration once per page.
 *
 * @returns {Promise<Object|null>} `{ apiKey, authDomain, projectId, tenantId }`, or null when the
 *   route reports a blank field, as in an environment without Firebase sign-in for this client.
 * @throws {Error} When the request fails or the response is not the four string fields, such as an
 *   error page; the failure is not cached, so a later call retries.
 */
function getFirebaseWebConfig() {
  if (!webConfigPromise) {
    webConfigPromise = axios.get(`${ process.env.REACT_APP_API_URL }/v1/auth/firebase-config`, { timeout: CONFIG_TIMEOUT_MS })
      .then(({ data }) => {
        const values = WEB_CONFIG_FIELDS.map((field) => data?.[field]);
        if (!data || typeof data !== 'object' || !values.every((value) => typeof value === 'string')) {
          throw new Error('/v1/auth/firebase-config did not return the Firebase web configuration');
        }
        if (!values.every(isNonEmptyString)) {
          return null;
        }
        const [apiKey, authDomain, projectId, tenantId] = values;
        return { apiKey, authDomain, projectId, tenantId };
      });
    webConfigPromise.catch(() => {
      webConfigPromise = undefined;
    });
  }
  return webConfigPromise;
}

/**
 * Returns the tenant-scoped Auth instance, initializing the SDK once per page.
 *
 * @returns {Promise<{auth: Object, tenantId: string}>} Firebase `Auth` and the configured tenant.
 * @throws {FirebaseSignedOutError} When the environment has no Firebase sign-in configured.
 * @throws {Error} When the configuration or the SDK cannot be loaded; not cached.
 */
function getFirebaseAuth() {
  if (!authPromise) {
    authPromise = (async () => {
      const webConfig = await getFirebaseWebConfig();
      if (!webConfig) {
        throw new FirebaseSignedOutError('the Firebase web configuration is not set');
      }
      const { tenantId, ...options } = webConfig;
      const [{ initializeApp }, { browserLocalPersistence, initializeAuth }] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
      ]);
      // Both return the existing instance when called again with the same options.
      const app = initializeApp(options, APP_NAME);
      const auth = initializeAuth(app, { persistence: browserLocalPersistence });
      auth.tenantId = tenantId;
      return { auth, tenantId };
    })();
    authPromise.catch(() => {
      authPromise = undefined;
    });
  }
  return authPromise;
}

/**
 * Waits for Firebase to restore any persisted sign-in, then returns the signed-in user. Reading
 * `currentUser` before the restore finishes would wrongly report a signed-out browser.
 *
 * @returns {Promise<Object|null>} The restored user, or null when this browser has no Firebase
 *   sign-in in the Vote Forward tenant.
 * @throws {Error} When the restore does not finish within `RESTORE_TIMEOUT_MS`, as with a corrupt
 *   stored sign-in, which the SDK never finishes restoring.
 */
async function getRestoredFirebaseUser() {
  const { auth, tenantId } = await getFirebaseAuth();
  await withTimeout(auth.authStateReady(), RESTORE_TIMEOUT_MS, 'Firebase did not finish restoring the sign-in');
  // A stored sign-in can belong to another tenant. Compare with the configuration, because
  // `auth.tenantId` is mutable.
  const user = auth.currentUser;
  return user && user.tenantId === tenantId ? user : null;
}

/**
 * Returns a newly minted ID token for the restored Firebase user. The user is reloaded first, so a
 * deleted or disabled account is caught before the exchange, and the forced refresh mints the
 * token from the account as it is now.
 *
 * @returns {Promise<{idToken: string, user: Object}>} The fresh ID token and its user.
 * @throws {FirebaseSignedOutError} When there is no usable signed-in user.
 * @throws {Error} Any other failure, which may be temporary.
 */
async function getFreshFirebaseIdToken() {
  const user = await getRestoredFirebaseUser();
  if (!user) {
    throw new FirebaseSignedOutError();
  }
  try {
    await user.reload();
    return { idToken: await user.getIdToken(true), user };
  } catch (err) {
    if (SIGNED_OUT_ERROR_CODES.includes(err?.code)) {
      throw new FirebaseSignedOutError(err.code);
    }
    throw err;
  }
}

/**
 * Reports whether any Firebase sign-in is persisted for the default app name. The API key in the
 * storage key comes from the server, so any key is matched rather than fetching it first.
 *
 * @returns {boolean} True when one may be persisted.
 */
function hasPersistedFirebaseUser() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PERSISTED_USER_KEY_PREFIX) && key.endsWith(PERSISTED_USER_KEY_SUFFIX)) {
        return true;
      }
    }
    return false;
  } catch {
    // Storage that cannot be read may still hold a sign-in, so assume it does.
    return true;
  }
}

/**
 * Signs this browser out of Firebase, in both apps, when it may hold a sign-in. When the SDK has not
 * started and nothing is persisted, there is nothing to sign out, so the SDK is not downloaded. A
 * failure, or a sign-out still pending after `SIGN_OUT_TIMEOUT_MS`, is logged rather than thrown, so
 * it cannot keep the VF session from being cleared; a pending sign-out may not finish before the
 * page leaves. The authorization-code callback instead propagates failures to stop the exchange.
 *
 * @param {Object} [options]
 * @param {boolean} [options.bestEffort=true] - Log failures instead of throwing.
 * @returns {Promise<void>}
 * @throws {Error} When sign-out fails and `bestEffort` is false.
 */
async function signOutOfFirebase({ bestEffort = true } = {}) {
  if (!authPromise && !hasPersistedFirebaseUser()) {
    return;
  }
  try {
    await withTimeout(
      getFirebaseAuth().then(({ auth }) => auth.signOut()),
      SIGN_OUT_TIMEOUT_MS,
      'Firebase sign-out did not finish',
    );
  } catch (err) {
    if (!bestEffort) {
      throw err;
    }
    // Without the web configuration this client cannot sign out of Firebase; any persisted sign-in stays.
    if (!(err instanceof FirebaseSignedOutError)) {
      logger.error('signOutOfFirebase: Firebase sign-out failed', { kind: err?.name, message: err?.message, code: err?.code });
    }
  }
}

export {
  FirebaseSignedOutError,
  getFreshFirebaseIdToken,
  signOutOfFirebase,
};
